import { CUSTOM_ELEMENTS_SCHEMA, OnInit, Component, inject } from '@angular/core';
import { Subscription, SubscriptionsService } from '../services/user/subscriptions.service';
import { Content } from "../services/admin/content.service";
import { RouterLink, Router } from '@angular/router';
import { UtilsPipe } from '../utils.pipe';
import { CommonModule, DatePipe } from '@angular/common';
import { actionsIconConfig, basicTableActionTypes } from '../../constants';
import { utcToCentralDisplay } from '../utils/timezone-utils';
import { CloudWatchRumService } from '../services/cloudwatch-rum.service';
import { AuthContextService } from '../services/auth-context.service';
import { LoadingComponent } from '../loading/loading.component';

@Component({
  selector: 'app-subscriptions',
  standalone: true,
  imports: [RouterLink, UtilsPipe, DatePipe, CommonModule, LoadingComponent],
  templateUrl: './subscriptions.component.html',
  styleUrl: './subscriptions.component.css',
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class SubscriptionsComponent implements OnInit {
  subscriptions: Subscription[] = [];
  basicTableActionTypes = basicTableActionTypes;
  actionsIconConfig = actionsIconConfig;
  isDeleteSubscriptionModalVisible = false;
  isNotificationModalVisible = false;
  subscriptionToDelete: Subscription | null = null;
  isLoading = false;
  alertMessage: string = '';
  alertVariant: 'error' | 'success' | 'warning' | 'info' = 'error';
  private cloudWatchRumService: CloudWatchRumService = inject(CloudWatchRumService);
  private authContextService: AuthContextService = inject(AuthContextService);

  constructor(private SubscriptionsService: SubscriptionsService, private router: Router) { }

  closeNotificationModal() {
    this.isNotificationModalVisible = false;
    this.alertMessage = '';
  }

  private showNotification(message: string, variant: 'success' | 'error') {
    this.alertVariant = variant;
    this.alertMessage = message;
    this.isNotificationModalVisible = true;
  }

  ngOnInit() {
    // Show success message passed via router state (e.g. after edit redirect)
    const navState = history.state;
    if (navState?.successMessage) {
      this.showNotification(navState.successMessage, 'success');
    }

    this.isLoading = true;

    this.SubscriptionsService.getUserSubscriptions().subscribe({
      next: (data) => {
        this.subscriptions = data;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  onSubscriptionClick(subscription: Subscription) {
    this.SubscriptionsService.setSelectedSubscription(subscription);
    const baseRoute = this.authContextService.hasInternalAccess ? '/internal/subscriptions' : '/subscriptions';
    this.router.navigate([baseRoute, this.SubscriptionsService.generateSubscriptionUriFromTitle(subscription)]);
  }

  onDownloadClick(storageURL: string, subscription: Subscription) {
    // Obfuscate sensitive data in the URL before sending to RUM
    const obfuscatedUrl = this.obfuscateUrl(storageURL);
    
    this.cloudWatchRumService.addCustomEvent('subscriptionDownload', {
      subscriptionId: subscription.subscriptionId,
      dashboardId: subscription.dashboardId,
      subscriptionUrl: obfuscatedUrl
    });
    
    this.SubscriptionsService.downloadFile(storageURL);
  }

  /**
   * Obfuscate AWS credentials in S3 Pre-Signed URLs
   * Masks sensitive AWS parameters like credentials, signatures, and session tokens
   * @param url The S3 Pre-Signed URL to obfuscate
   * @returns The obfuscated URL with sensitive AWS data masked
   */
  private obfuscateUrl(url: string): string {
    // Obfuscate AWS S3 Pre-Signed URL parameters that contain sensitive credentials
    return url
      // Mask X-Amz-Credential (contains AWS access key and other sensitive info)
      .replace(/([?&])(X-Amz-Credential)=([^&]*)/gi, '$1$2=***')
      // Mask X-Amz-Signature (the request signature)
      .replace(/([?&])(X-Amz-Signature)=([^&]*)/gi, '$1$2=***')
      // Mask X-Amz-Security-Token (session token if using temporary credentials)
      .replace(/([?&])(X-Amz-Security-Token)=([^&]*)/gi, '$1$2=***')
      // Also mask any other potentially sensitive parameters
      .replace(/([?&])(authorization|token|secret|key|password)=([^&]*)/gi, '$1$2=***');
  }

  onViewReportClick(subscription: Subscription) {
    // Navigate to the embedded dashboard using the subscription's dashboardId
  
    if (subscription.dashboardId) {
      const baseRoute = this.authContextService.hasInternalAccess ? '/internal/embedded-dashboard' : '/embedded-dashboard';
      this.router.navigate([baseRoute, subscription.dashboardId], {
        queryParams: { c: btoa(JSON.stringify(subscription)), contentId: subscription.contentid },
      });
    } else {
      console.warn('No dashboardId available for subscription:', subscription);
    }
  }

  /**
   * Format timestamp for display in Central time
   * @param timestamp UTC timestamp from API
   * @returns Formatted string in Central time
   */
  formatTimestamp(timestamp: string): string {
    return utcToCentralDisplay(timestamp, 'full');
  }

  onEditClick(subscription: Subscription) {
    this.SubscriptionsService.setSelectedSubscription(subscription);
    const baseRoute = this.authContextService.hasInternalAccess ? '/internal/edit-subscription' : '/edit-subscription';
    this.router.navigate([baseRoute], {
      queryParams: { pageMode: 'edit' }
    });
  }
  onOverflowItemSelected(event: Event, sub: Subscription) {
    // TODO: Add cloudwatch rum call here:

    // this.cloudWatchRumService.addCustomEvent('reportsAndDashboardsItemSelected', { payload });
    switch ((event as CustomEvent).detail.action) {
      case 'delete':
        this.cloudWatchRumService.addCustomEvent('subscription_deleted', {
          contentId: sub.contentid,
        });
        this.SubscriptionsService.deleteSubscription(sub).subscribe({
          next: (response) => {
            this.subscriptions = this.subscriptions.filter((subscription) => subscription.subscriptionId !== sub.subscriptionId);
            this.showNotification('Subscription deleted successfully.', 'success');
          },
          error: (error) => {
            console.error('Error deleting subscription', error);
            this.showNotification('Failed to delete subscription. Please try again.', 'error');
          },
          complete: () => { }
        })
        break;
      case 'edit':
      default:
        this.onEditClick(sub);
        break;
    }
  }
  onOverflowItemClicked(event: any, sub: Subscription) {
    switch (event.name) {
      case 'Delete':
        this.isDeleteSubscriptionModalVisible = true;
        this.subscriptionToDelete = sub;
        break;
      case 'Edit':
      default:
        this.onEditClick(sub);
        break;
    }
  }
  onModalDeleteButtonClicked() {
            this.cloudWatchRumService.addCustomEvent('subscription_deleted', {
              contentId: this.subscriptionToDelete?.contentid,
            });
            this.SubscriptionsService.deleteSubscription(this.subscriptionToDelete).subscribe({
          next: (response) => {
            this.subscriptions = this.subscriptions.filter((subscription) => subscription.subscriptionId !== this.subscriptionToDelete?.subscriptionId);
            this.showNotification('Subscription deleted successfully.', 'success');
          },
          error: (error) => {
            console.error('Error deleting subscription', error);
            this.showNotification('Failed to delete subscription. Please try again.', 'error');
            this.isDeleteSubscriptionModalVisible = false;
            this.subscriptionToDelete = null;
          },
          complete: () => { 
            this.isDeleteSubscriptionModalVisible = false;
            this.subscriptionToDelete = null;
          }
        })
    
  }
  onModalDeleteCancelButtonClicked() {
    this.isDeleteSubscriptionModalVisible = false;
    this.subscriptionToDelete = null;
  }
}
